import {
  BlockWithOverrides,
  isBlockWithTitleOverrides,
} from '../schema/type-casts'
import type {
  Block,
  InstanceBlock,
  ReferenceBlock,
  RichTextBlock,
  Variant,
} from '../schema/block'
import type { PartialDeep } from 'type-fest'
import { getApiNameFromTitle } from '../../api-kv'
import { AtLeast } from '.'
import { invariant } from '@epic-web/invariant'
import { isObjectAsWeCommonlyCallIt } from '@basehub/utils/object'

const DEBUG = false
// eslint-disable-next-line no-console
const logger: typeof console.log = (...args) => DEBUG && console.log(...args)

export type VariantData = { setId: string; variantId: string }

// Key Helpers
export function getVariantsKey(selectedVariants: VariantData[]): string {
  return selectedVariants
    .map((v) => `${v.setId}-${v.variantId}`)
    .sort()
    .join('/')
}

export function getVariantsDataFromKey(key: string): VariantData[] {
  return key.split('/').map((v) => {
    const [setId, variantId] = v.split('-')
    invariant(setId && variantId, `Invalid variant key: ${key}`)
    return { setId, variantId }
  })
}

type VariantOverrideItem<B extends BlockWithOverrides | InstanceBlock> =
  NonNullable<B['variantOverrides']>[string]
type OverrideMatcher<
  B extends BlockWithOverrides | InstanceBlock = BlockWithOverrides,
> = {
  override: VariantOverrideItem<B>
  matches: number
}

function getBestMatch<B extends BlockWithOverrides | InstanceBlock>({
  variantsToFilterBy,
  overrides,
}: {
  variantsToFilterBy: VariantData[]
  overrides: [string, VariantOverrideItem<B>][]
}): NonNullable<B['variantOverrides']>[string] | null {
  const maxMatches = variantsToFilterBy.length
  let bestMatch: OverrideMatcher<B> | null = null

  for (const [variantsKey, override] of overrides) {
    const variantsFromKey = getVariantsDataFromKey(variantsKey)
    if (variantsFromKey.length > maxMatches) continue
    let matches = 0
    for (const variantData of variantsFromKey) {
      if (
        variantsToFilterBy.some(
          (v2) =>
            v2.setId === variantData.setId &&
            v2.variantId === variantData.variantId
        )
      ) {
        matches++
      } else if (
        variantsToFilterBy.some((v2) => v2.setId === variantData.setId)
      ) {
        matches = 0
        logger(
          'Override discarded because has same set with another variant:',
          override
        )

        break
      }
    }
    if (matches >= (bestMatch?.matches ?? 0)) {
      bestMatch = { override, matches }
    }
    if (matches === maxMatches && matches === variantsFromKey.length) {
      logger('Returning early with bestMatch:', bestMatch)
      break
    }
  }

  if (bestMatch && bestMatch.matches !== 0) {
    logger('Best match found:', bestMatch)
    return bestMatch.override
  }

  return null
}
/**
  Iterates through the block overrides and returns the best available match for the selected branches.
  This function assumes both `variantOverrides` and `variantsToFilterBy` ignored default variants.
  ***It's crucial that default variants are filtered out before calling this function**,
  or it won't return the right matches.
  If no match is found, it returns the default value of the block.
 */
export const getValueBasedOnSelectedVariants = <
  B extends AtLeast<BlockWithOverrides, 'type' | 'variantOverrides' | 'value'>,
>({
  variantsToFilterBy,
  block,
  merge = true,
}: {
  block: B
  variantsToFilterBy: VariantData[]
  merge?: boolean
}): B['value'] => {
  logger('Will find best match for:', {
    block,
    variantsToFilterBy,
  })
  const overridesEntries = Object.entries(block.variantOverrides || {})
  if (variantsToFilterBy.length && overridesEntries[0]) {
    const bestMatch = getBestMatch({
      variantsToFilterBy,
      overrides: overridesEntries,
    })

    if (bestMatch) {
      logger('Best match found:', bestMatch)
      if (
        isObjectAsWeCommonlyCallIt(bestMatch.value) &&
        isObjectAsWeCommonlyCallIt(block.value)
      ) {
        return merge
          ? { ...block.value, ...(bestMatch.value as Partial<B['value']>) }
          : (bestMatch.value as B['value'])
      }
      return bestMatch.value as B['value']
    }
  }
  return block.value
}

export const getTitleBasedOnSelectedVariants = ({
  variantsToFilterBy,
  block,
  parent,
}: {
  block: Block
  parent: Block | null | undefined
  variantsToFilterBy: VariantData[]
}): { title: Block['title']; slug: Block['slug'] } => {
  if (!isBlockWithTitleOverrides(block, parent)) {
    return { title: block.title, slug: block.slug }
  }

  logger('Will find best match for:', {
    block,
    variantsToFilterBy,
  })

  const overridesEntries = Object.entries(block.variantOverrides || {})
  if (variantsToFilterBy.length && overridesEntries[0]) {
    const bestMatch = getBestMatch<InstanceBlock>({
      variantsToFilterBy,
      overrides: overridesEntries,
    })
    if (bestMatch) {
      logger('Best match found:', bestMatch)
      return {
        title: bestMatch.title,
        slug: bestMatch.slug,
      }
    }
  }
  return { title: block.title, slug: block.slug }
}

type BlockUpdateOverrideBase<B extends BlockWithOverrides> = AtLeast<B, 'type'>

type BlockUpdateOverrideWithValue<B extends BlockWithOverrides> =
  BlockUpdateOverrideBase<B> & {
    value: B['value']
  }

type BlockUpdateOverrideWithVariants<B extends BlockWithOverrides> =
  BlockUpdateOverrideBase<B> & {
    variantOverrides: B['variantOverrides']
  }

type BlockUpdateOverride<B extends BlockWithOverrides> =
  | BlockUpdateOverrideWithValue<B>
  | BlockUpdateOverrideWithVariants<B>

export function getBlockUpdateOverride<B extends BlockWithOverrides>({
  newValue,
  block,
  selectedVariants,
  extend,
}: {
  newValue: PartialDeep<B['value']>
  block: B
  selectedVariants: VariantData[]
  extend?: Omit<PartialDeep<B>, 'value' | 'type'>
}): BlockUpdateOverride<B> {
  if (selectedVariants.length) {
    const key = getVariantsKey(selectedVariants)

    switch (block.type) {
      case 'reference': {
        const parsedValue = newValue as PartialDeep<ReferenceBlock['value']>
        const fallbackValue =
          block.variantOverrides?.[key]?.value ?? block.value

        const overrideValue = withoutUndefinedValues({
          targetBlockIds:
            parsedValue.targetBlockIds ?? fallbackValue.targetBlockIds,
        })
        return {
          type: block.type,
          ...(overrideValue
            ? {
                variantOverrides: {
                  [key]: {
                    value: overrideValue,
                  },
                },
              }
            : {}),
          ...extend,
        } as BlockUpdateOverrideWithVariants<B>
      }
      case 'media':
      case 'code-snippet': {
        const fallbackValue =
          block.variantOverrides?.[key]?.value ?? block.value
        return {
          type: block.type,
          variantOverrides: {
            [key]: {
              value: {
                ...fallbackValue,
                ...(newValue as typeof fallbackValue),
              },
            },
          },
          ...extend,
        } as BlockUpdateOverrideWithVariants<B>
      }
      case 'rich-text': {
        const parsedValue = newValue as PartialDeep<RichTextBlock['value']>
        const overrideValue = withoutUndefinedValues({
          state: parsedValue.state,
          transformations: parsedValue.transformations,
        })

        return {
          type: block.type,
          value: withoutUndefinedValues({
            lastRevertedAt: parsedValue.lastRevertedAt,
          }),
          ...(overrideValue
            ? {
                variantOverrides: {
                  [key]: {
                    value: overrideValue,
                  },
                },
              }
            : {}),
          ...extend,
        } as BlockUpdateOverrideWithVariants<B>
      }
    }

    return {
      type: block.type,
      variantOverrides: {
        [key]: {
          value: newValue,
        },
      },
      ...extend,
    } as BlockUpdateOverrideWithVariants<B>
  }

  return {
    type: block.type,
    value: newValue,
    ...extend,
  } as unknown as BlockUpdateOverrideWithValue<B>
}

export function getTitleUpdateOverride(data: {
  slug: Block['slug']
  block: Block
  parent: Block | null | undefined
  selectedVariants: VariantData[]
}): PartialDeep<Block>
export function getTitleUpdateOverride(data: {
  title: Block['title']
  block: Block
  parent: Block | null | undefined
  selectedVariants: VariantData[]
}): PartialDeep<Block>

export function getTitleUpdateOverride({
  title,
  block,
  parent,
  selectedVariants,
  slug,
}: {
  title?: Block['title']
  slug?: Block['slug']
  block: Block
  parent: Block | null | undefined
  selectedVariants: VariantData[]
}): PartialDeep<Block> {
  if (!isBlockWithTitleOverrides(block, parent) || !selectedVariants.length) {
    return {
      type: block.type,
      title,
      slug,
    }
  }

  const key = getVariantsKey(selectedVariants)
  const previous = block.variantOverrides?.[key]
  return {
    type: block.type,
    variantOverrides: {
      [key]: {
        title: title ?? previous?.title,
        slug: slug ?? previous?.slug,
      },
    },
  }
}

export function hasSameVariants(a: VariantData[], b: VariantData[]): boolean {
  if (a.length !== b.length) return false
  return getVariantsKey(a) === getVariantsKey(b)
}

// API Name Uniqueness
export function checkIfVariantNameExists(
  variant: Pick<Variant, 'id' | 'apiName'>,
  siblings: Pick<Variant, 'apiName' | 'id'>[]
): boolean {
  return siblings.some(
    (v) => v.id !== variant.id && v.apiName === variant.apiName
  )
}

export function getUniqueVariantName(
  variant: AtLeast<Variant, 'label' | 'id' | 'isDefault'> & {
    apiName?: string
  },
  siblings: Pick<Variant, 'apiName' | 'id' | 'label'>[]
): { apiName: string; label: string } {
  let apiName = variant.apiName
  let label = variant.label

  if (variant.apiName) {
    if (
      siblings.some((v) => v.id !== variant.id && v.apiName === variant.apiName)
    ) {
      throw new Error("Can't have duplicate API names")
    }
  }

  if (!label) {
    if (variant.isDefault) {
      label = 'Default'
    } else {
      label = 'Untitled'
    }
  }

  if (!apiName) {
    apiName = getApiNameFromTitle(label)
    if (siblings.some((v) => v.id !== variant.id && v.apiName === apiName)) {
      apiName = `${apiName}_${variant.id.slice(0, 4)}`
    }
  }
  return { apiName, label }
}

export function withUniqueApiNames(variants: Variant[]): Variant[] {
  const copy = structuredClone(variants)
  for (const variant of copy) {
    const result = getUniqueVariantName(variant, copy)
    variant.apiName = result.apiName
    variant.label = result.label
  }
  return copy
}

function withoutUndefinedValues<T extends Record<string, unknown>>(
  obj: T
): Partial<T> | undefined {
  const entries = Object.entries(obj).filter(
    ([_, value]) => value !== undefined
  )

  if (entries.length === 0) return undefined
  return Object.fromEntries(entries) as Partial<T>
}
